import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { listAllStudents } from '../api/studentsApi';
import { createFeeReceipt, listFeeReceipts, deleteFeeReceipt } from '../api/feeReceiptsApi';
import {
  listBookInventory,
  saveBookInventory,
  deleteBookInventory,
  listDressInventory,
  saveDressInventory,
  deleteDressInventory,
} from '../api/inventoryApi';
import { AppHeader } from './LoginPanel';
import MultiSearchableSelect from './MultiSearchableSelect';
import SearchableSelect from './SearchableSelect';
import { classOptions } from '../constants/classes';
import { matchesSearch } from '../utils/searchFilter';
import CoordinatorAttendanceSection from './CoordinatorAttendanceSection';
import InventoryIssueSection from './InventoryIssueSection';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const DRESS_TYPES = [
  { value: 'Shirt', label: 'Shirt' },
  { value: 'Pant', label: 'Pant' },
  { value: 'Skirt', label: 'Skirt' },
  { value: 'Sweater', label: 'Sweater' },
  { value: 'Tie', label: 'Tie' },
  { value: 'Belt', label: 'Belt' },
  { value: 'Blazer', label: 'Blazer' },
];

const DRESS_SIZES = ['20', '22', '24', '26', '28', '30', '32', '34', '36', '38', '40', 'S', 'M', 'L', 'XL'].map((s) => ({
  value: s,
  label: s,
}));

export default function CoordinatorPanel({ embedded = false, adminMode = false }) {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState('attendance');
  const [students, setStudents] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [books, setBooks] = useState([]);
  const [dresses, setDresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [receiptForm, setReceiptForm] = useState({
    paymentMethod: 'cash',
    notes: '',
    amountPerStudent: '',
    monthsPaid: '',
    description: 'Fee payment',
  });

  const [bookForm, setBookForm] = useState({ className: '1', subject: '', count: '' });
  const [dressForm, setDressForm] = useState({ dressType: 'Shirt', size: '28', count: '' });
  const [bookFilter, setBookFilter] = useState('');
  const [dressFilter, setDressFilter] = useState('');

  const classOpts = useMemo(() => classOptions(), []);
  const studentOpts = useMemo(
    () =>
      students.map((s) => ({
        value: s.studentId,
        label: `${s.studentName} · adm ${s.admno} · class ${s.class || '—'}`,
      })),
    [students]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const results = await Promise.allSettled([
        listAllStudents(),
        listFeeReceipts(),
        listBookInventory(),
        listDressInventory(),
      ]);
      const pick = (i, fallback) => (results[i].status === 'fulfilled' ? results[i].value : fallback);
      if (results.some((r) => r.status === 'rejected')) {
        const firstErr = results.find((r) => r.status === 'rejected');
        setErr(firstErr?.reason?.message || 'Some data could not be loaded.');
      }
      setStudents(pick(0, []));
      setReceipts(pick(1, []));
      setBooks(pick(2, []));
      setDresses(pick(3, []));
    } catch (e) {
      setErr(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateReceipt = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    if (selectedStudentIds.length === 0) {
      setErr('Select at least one student.');
      return;
    }
    const amount = Number(receiptForm.amountPerStudent);
    if (!amount || amount <= 0) {
      setErr('Enter a valid amount per student.');
      return;
    }
    try {
      const lines = selectedStudentIds.map((id) => {
        const s = students.find((x) => x.studentId === id);
        return {
          studentId: id,
          studentName: s?.studentName || id,
          amount,
          monthsPaid: receiptForm.monthsPaid,
          description: receiptForm.description,
        };
      });
      const result = await createFeeReceipt({
        lines,
        paymentMethod: receiptForm.paymentMethod,
        notes: receiptForm.notes,
        createdByUid: user?.uid,
        createdByName: profile?.name,
      });
      setMsg(`Receipt created: ${result.lineCount} student(s), total ₹${result.totalAmount}`);
      setSelectedStudentIds([]);
      setReceiptForm((f) => ({ ...f, amountPerStudent: '', monthsPaid: '', notes: '' }));
      await load();
    } catch (ex) {
      setErr(ex.message || 'Could not create receipt');
    }
  };

  const handleSaveBook = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      await saveBookInventory(bookForm);
      setMsg(`Book stock saved: ${bookForm.subject} (class ${bookForm.className})`);
      setBookForm((f) => ({ ...f, subject: '', count: '' }));
      await load();
    } catch (ex) {
      setErr(ex.message || 'Could not save book inventory');
    }
  };

  const handleSaveDress = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      await saveDressInventory(dressForm);
      setMsg(`Dress stock saved: ${dressForm.dressType} size ${dressForm.size}`);
      setDressForm((f) => ({ ...f, count: '' }));
      await load();
    } catch (ex) {
      setErr(ex.message || 'Could not save dress inventory');
    }
  };

  const filteredBooks = useMemo(
    () => books.filter((b) => matchesSearch(bookFilter, [b.class, b.subject, String(b.count)])),
    [books, bookFilter]
  );
  const filteredDresses = useMemo(
    () => dresses.filter((d) => matchesSearch(dressFilter, [d.dressType, d.size, String(d.count)])),
    [dresses, dressFilter]
  );

  const tabButtons = (compact) => (
    <>
      <button type="button" className={`mps-tab ${tab === 'attendance' ? 'mps-tab-active' : ''}`} onClick={() => setTab('attendance')}>
        Attendance
      </button>
      <button type="button" className={`mps-tab ${tab === 'receipts' ? 'mps-tab-active' : ''}`} onClick={() => setTab('receipts')}>
        {compact ? 'Fees' : 'Fee receipts'}
      </button>
      <button type="button" className={`mps-tab ${tab === 'issue' ? 'mps-tab-active' : ''}`} onClick={() => setTab('issue')}>
        {compact ? 'Issue' : 'Issue stock'}
      </button>
      <button type="button" className={`mps-tab ${tab === 'books' ? 'mps-tab-active' : ''}`} onClick={() => setTab('books')}>
        {compact ? 'Books' : 'Book inventory'}
      </button>
      <button type="button" className={`mps-tab ${tab === 'dress' ? 'mps-tab-active' : ''}`} onClick={() => setTab('dress')}>
        {compact ? 'Dress' : 'Dress inventory'}
      </button>
    </>
  );

  const receiptTotalPreview =
    selectedStudentIds.length > 0 && receiptForm.amountPerStudent
      ? selectedStudentIds.length * Number(receiptForm.amountPerStudent)
      : 0;

  const content = (
    <>
      <div className="mps-tabs-scroll">
        <div className={`mps-tabs mps-tabs-coordinator${embedded ? ' mps-tabs-sub' : ''}`}>{tabButtons(embedded)}</div>
      </div>

      <div className="mps-field" style={{ marginBottom: 12, maxWidth: 280 }}>
        <label htmlFor="coord-tab-jump">Jump to section</label>
        <select
          id="coord-tab-jump"
          className="mps-select-native"
          value={tab}
          onChange={(e) => setTab(e.target.value)}
        >
          <option value="attendance">Attendance</option>
          <option value="receipts">Fee receipts</option>
          <option value="issue">Issue stock</option>
          <option value="books">Book inventory</option>
          <option value="dress">Dress inventory</option>
        </select>
      </div>

      {err ? <div className="mps-msg mps-msg-error">{err}</div> : null}
      {msg ? <div className="mps-msg mps-msg-success">{msg}</div> : null}

        {tab === 'attendance' ? (
          <CoordinatorAttendanceSection setMsg={setMsg} setErr={setErr} allowDelete />
        ) : tab === 'issue' ? (
          <InventoryIssueSection
            students={students}
            books={books}
            dresses={dresses}
            user={user}
            profile={profile}
            setMsg={setMsg}
            setErr={setErr}
            onIssued={load}
            allowDelete={adminMode}
          />
        ) : tab === 'receipts' ? (
          <>
            <div className="mps-card">
              <h2>Create fee receipt</h2>
              <p className="mps-muted" style={{ marginTop: 0 }}>
                Select <strong>one or more students</strong> from the dropdown. One receipt can cover multiple students (e.g. siblings).
              </p>
              <form onSubmit={handleCreateReceipt}>
                <div className="mps-field" style={{ marginBottom: 12 }}>
                  <label>Students (multi-select)</label>
                  <MultiSearchableSelect
                    value={selectedStudentIds}
                    onChange={setSelectedStudentIds}
                    options={studentOpts}
                    placeholder="Search and select students…"
                  />
                </div>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Amount per student (₹)</label>
                    <input
                      type="number"
                      value={receiptForm.amountPerStudent}
                      onChange={(e) => setReceiptForm({ ...receiptForm, amountPerStudent: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mps-field">
                    <label>Payment method</label>
                    <SearchableSelect
                      value={receiptForm.paymentMethod}
                      onChange={(v) => setReceiptForm({ ...receiptForm, paymentMethod: v })}
                      options={PAYMENT_METHODS}
                      placeholder="Method…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Months paid (comma separated)</label>
                    <input
                      placeholder="Apr-2026, May-2026"
                      value={receiptForm.monthsPaid}
                      onChange={(e) => setReceiptForm({ ...receiptForm, monthsPaid: e.target.value })}
                    />
                  </div>
                  <div className="mps-field">
                    <label>Description</label>
                    <input
                      value={receiptForm.description}
                      onChange={(e) => setReceiptForm({ ...receiptForm, description: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mps-field" style={{ marginTop: 12 }}>
                  <label>Notes (optional)</label>
                  <input value={receiptForm.notes} onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })} />
                </div>
                {receiptTotalPreview > 0 ? (
                  <p className="mps-muted" style={{ marginTop: 8 }}>
                    Receipt total: <strong>₹{receiptTotalPreview}</strong> ({selectedStudentIds.length} student(s))
                  </p>
                ) : null}
                <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
                  Create receipt
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>Recent receipts ({receipts.length})</h2>
              {loading ? (
                <p>Loading…</p>
              ) : receipts.length === 0 ? (
                <p className="mps-muted">No receipts yet.</p>
              ) : (
                <table className="mps-table">
                  <thead>
                    <tr>
                      <th>Receipt no.</th>
                      <th>Date</th>
                      <th>Students</th>
                      <th>Total</th>
                      <th>Method</th>
                      {adminMode ? <th>Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.slice(0, 30).map((r) => (
                      <tr key={r.id}>
                        <td>{r.receiptNo || r.id}</td>
                        <td>{r.date || (r.paidAt || '').slice(0, 10)}</td>
                        <td>
                          {(r.lines || []).map((l) => l.studentName).join(', ') || '—'}
                        </td>
                        <td>₹{r.totalAmount}</td>
                        <td>{r.paymentMethod || '—'}</td>
                        {adminMode ? (
                          <td>
                            <button
                              type="button"
                              className="mps-btn mps-btn-sm mps-btn-danger"
                              onClick={async () => {
                                if (!window.confirm(`Delete fee receipt ${r.receiptNo || r.id}?`)) return;
                                await deleteFeeReceipt(r.id);
                                setMsg('Fee receipt deleted.');
                                await load();
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : tab === 'books' ? (
          <>
            <div className="mps-card">
              <h2>Add / update book stock</h2>
              <form onSubmit={handleSaveBook}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Class</label>
                    <SearchableSelect
                      value={bookForm.className}
                      onChange={(v) => setBookForm({ ...bookForm, className: v })}
                      options={classOpts}
                      placeholder="Class…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Subject name</label>
                    <input
                      value={bookForm.subject}
                      onChange={(e) => setBookForm({ ...bookForm, subject: e.target.value })}
                      placeholder="e.g. Mathematics, Hindi"
                      required
                    />
                  </div>
                  <div className="mps-field">
                    <label>Book count</label>
                    <input
                      type="number"
                      min="0"
                      value={bookForm.count}
                      onChange={(e) => setBookForm({ ...bookForm, count: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
                  Save book stock
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>Book inventory ({books.length})</h2>
              <input
                type="search"
                className="mps-table-filter"
                value={bookFilter}
                onChange={(e) => setBookFilter(e.target.value)}
                placeholder="Search class, subject…"
              />
              <table className="mps-table">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Count</th>
                    {adminMode ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.map((b) => (
                    <tr key={b.id}>
                      <td>{b.class}</td>
                      <td>{b.subject}</td>
                      <td>{b.count}</td>
                      {adminMode ? (
                        <td>
                          <button
                            type="button"
                            className="mps-btn mps-btn-sm mps-btn-danger"
                            onClick={async () => {
                              if (!window.confirm(`Delete ${b.subject} (${b.class})?`)) return;
                              await deleteBookInventory(b.id);
                              setMsg('Book stock deleted.');
                              await load();
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="mps-card">
              <h2>Add / update dress stock</h2>
              <form onSubmit={handleSaveDress}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Dress type</label>
                    <SearchableSelect
                      value={dressForm.dressType}
                      onChange={(v) => setDressForm({ ...dressForm, dressType: v })}
                      options={DRESS_TYPES}
                      placeholder="Type…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Size</label>
                    <SearchableSelect
                      value={dressForm.size}
                      onChange={(v) => setDressForm({ ...dressForm, size: v })}
                      options={DRESS_SIZES}
                      placeholder="Size…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Count</label>
                    <input
                      type="number"
                      min="0"
                      value={dressForm.count}
                      onChange={(e) => setDressForm({ ...dressForm, count: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
                  Save dress stock
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>Dress inventory ({dresses.length})</h2>
              <input
                type="search"
                className="mps-table-filter"
                value={dressFilter}
                onChange={(e) => setDressFilter(e.target.value)}
                placeholder="Search type, size…"
              />
              <table className="mps-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Count</th>
                    {adminMode ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {filteredDresses.map((d) => (
                    <tr key={d.id}>
                      <td>{d.dressType}</td>
                      <td>{d.size}</td>
                      <td>{d.count}</td>
                      {adminMode ? (
                        <td>
                          <button
                            type="button"
                            className="mps-btn mps-btn-sm mps-btn-danger"
                            onClick={async () => {
                              if (!window.confirm(`Delete ${d.dressType} size ${d.size}?`)) return;
                              await deleteDressInventory(d.id);
                              setMsg('Dress stock deleted.');
                              await load();
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="mps-app">
      <AppHeader title="Coordinator dashboard" user={user} profile={profile} onSignOut={() => auth && signOut(auth)} />
      <main className="mps-main">{content}</main>
    </div>
  );
}
